/**
 * Serialize / parse exhibition pre-order selections stored in preorder_notes.
 */

export function parseExhibitionPreorderNotes(raw) {
  const text = String(raw || '').trim();
  if (!text) return null;
  if (text.startsWith('{')) {
    try {
      const data = JSON.parse(text);
      if (data?.v === 1 && Array.isArray(data.items)) {
        return {
          v: 1,
          items: data.items
            .map((item) => ({
              id: String(item?.id || '').trim(),
              name: String(item?.name || '').trim(),
              category: String(item?.category || '').trim(),
              subcategory: String(item?.subcategory || '').trim(),
              qty: Math.max(0, Number(item?.qty) || 0),
              image: String(item?.image || '').trim(),
              unit_price:
                item?.unit_price != null && item?.unit_price !== '' && Number.isFinite(Number(item.unit_price))
                  ? Math.max(0, Number(item.unit_price))
                  : null,
            }))
            .filter((item) => item.id && item.qty > 0),
          note: String(data?.note || '').trim(),
        };
      }
    } catch {
      /* legacy free text */
    }
  }
  return { v: 0, text };
}

export function serializeExhibitionPreorder({ items = [], note = '' } = {}) {
  const list = (Array.isArray(items) ? items : [])
    .filter((item) => item && Number(item.qty) > 0)
    .map((item) => {
      const row = {
        id: String(item.id || '').trim(),
        name: String(item.name || '').trim(),
        category: String(item.category || '').trim(),
        subcategory: String(item.subcategory || '').trim(),
        qty: Math.max(1, Number(item.qty) || 1),
        image: String(item.image || '').trim(),
      };
      if (item.unit_price != null && item.unit_price !== '' && Number.isFinite(Number(item.unit_price))) {
        row.unit_price = Math.max(0, Number(item.unit_price));
      }
      return row;
    });
  const extra = String(note || '').trim();
  if (list.length === 0 && !extra) return '';
  return JSON.stringify({ v: 1, items: list, note: extra });
}

export function exhibitionPreorderSelectionCount(raw) {
  const parsed = parseExhibitionPreorderNotes(raw);
  if (!parsed) return 0;
  if (parsed.v === 1) {
    return parsed.items.reduce((sum, item) => sum + item.qty, 0);
  }
  return parsed.text ? 1 : 0;
}

export function formatExhibitionPreorderSummary(raw) {
  const parsed = parseExhibitionPreorderNotes(raw);
  if (!parsed) return '';
  if (parsed.v === 1) {
    const lines = parsed.items.map((item) => `${item.qty}× ${item.name}`);
    if (parsed.note) lines.push(parsed.note);
    return lines.join(' · ');
  }
  return parsed.text;
}

/** Effective unit price: saved at sign-up, else catalog sale/regular price. */
export function exhibitionPreorderItemUnitPrice(item, catalogProduct) {
  if (item?.unit_price != null && item.unit_price !== '' && Number.isFinite(Number(item.unit_price))) {
    return Math.max(0, Number(item.unit_price));
  }
  if (!catalogProduct) return null;
  const price = Number(catalogProduct.price || 0);
  const rawSale =
    catalogProduct.sale_price != null && catalogProduct.sale_price !== ''
      ? Number(catalogProduct.sale_price)
      : null;
  if (rawSale != null && Number.isFinite(rawSale) && rawSale > 0 && rawSale < price) {
    return rawSale;
  }
  return price;
}

export function exhibitionPreorderItemLineTotal(item, catalogProduct) {
  const unit = exhibitionPreorderItemUnitPrice(item, catalogProduct);
  if (unit == null || !Number.isFinite(unit)) return null;
  return unit * Math.max(0, Number(item?.qty) || 0);
}

/**
 * @param {{ v: number, items: Array }|null} parsed
 * @param {(item: object) => object|null} resolveCatalogProduct
 */
export function exhibitionPreorderTotals(parsed, resolveCatalogProduct) {
  if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.items)) {
    return { pieceCount: 0, subtotal: 0, pricedLines: 0, totalLines: 0 };
  }
  let pieceCount = 0;
  let subtotal = 0;
  let pricedLines = 0;
  parsed.items.forEach((item) => {
    pieceCount += item.qty;
    const product = resolveCatalogProduct(item);
    const lineTotal = exhibitionPreorderItemLineTotal(item, product);
    if (lineTotal != null) {
      subtotal += lineTotal;
      pricedLines += 1;
    }
  });
  return {
    pieceCount,
    subtotal,
    pricedLines,
    totalLines: parsed.items.length,
  };
}

/**
 * List/inbox summary for a raw preorder_notes string.
 * @param {string} raw
 * @param {(item: object) => object|null} [resolveCatalogProduct]
 * @returns {{ hasPreorder: boolean, pieceCount: number, subtotal: number, allPriced: boolean, legacyNote: boolean }}
 */
export function exhibitionPreorderMoneyFromRaw(raw, resolveCatalogProduct) {
  const parsed = parseExhibitionPreorderNotes(raw);
  if (!parsed) {
    return { hasPreorder: false, pieceCount: 0, subtotal: 0, allPriced: true, legacyNote: false };
  }
  if (parsed.v !== 1) {
    const hasText = Boolean(String(parsed.text || '').trim());
    return {
      hasPreorder: hasText,
      pieceCount: 0,
      subtotal: 0,
      allPriced: true,
      legacyNote: hasText,
    };
  }
  const resolve = typeof resolveCatalogProduct === 'function' ? resolveCatalogProduct : () => null;
  const totals = exhibitionPreorderTotals(parsed, resolve);
  const hasNote = Boolean(String(parsed.note || '').trim());
  return {
    hasPreorder: totals.totalLines > 0 || hasNote,
    pieceCount: totals.pieceCount,
    subtotal: totals.subtotal,
    allPriced: totals.totalLines === 0 || totals.pricedLines === totals.totalLines,
    legacyNote: false,
  };
}

/**
 * Short label for admin list rows, e.g. "₹12,000 · 3 pcs" or "2 pcs".
 * @param {string} raw
 * @param {(item: object) => object|null} [resolveCatalogProduct]
 * @param {(n: number) => string} [formatMoney]
 */
export function formatExhibitionPreorderListMoney(raw, resolveCatalogProduct, formatMoney) {
  const money = exhibitionPreorderMoneyFromRaw(raw, resolveCatalogProduct);
  if (!money.hasPreorder) return '';
  const parts = [];
  if (money.subtotal > 0 && typeof formatMoney === 'function') {
    parts.push(formatMoney(money.subtotal));
  } else if (money.subtotal > 0) {
    parts.push(`\u20B9${Number(money.subtotal).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
  }
  if (money.pieceCount > 0) {
    parts.push(`${money.pieceCount} pc${money.pieceCount === 1 ? '' : 's'}`);
  } else if (money.legacyNote && parts.length === 0) {
    return 'Note';
  }
  return parts.join(' · ');
}
